/**
 * §4.3 capabilities and the §4.4 role baseline matrix.
 *
 * This file is the TypeScript mirror of `role_baseline()` in
 * `packages/db/drizzle/0002_permission_functions.sql`. The database copy is the
 * one that decides anything — it sits inside the `actor` CTE of every
 * resolver — and this copy exists so the admin UI can say *why* a role cannot
 * do something without a round trip. `capabilities.test.ts` asserts the two are
 * identical in both directions; two copies of a matrix that disagree are worse
 * than either copy alone.
 */

import type { UserRole } from '@matchbox/db';

/**
 * §4.3, in table order, plus phase 2 §6's two. "Roughly twenty, deliberately.
 * It is not a policy engine." Later phases add domain capabilities when their
 * phase arrives, and these two are that happening rather than an exception to
 * it.
 */
export const CAPABILITIES = [
  'document.view',
  'document.comment',
  'document.suggest',
  'document.edit',
  'document.publish',
  'document.approve',
  'file.upload',
  'file.delete',
  'node.create',
  'node.delete',
  'space.manage',
  'space.grant',
  /**
   * Reading the directory, which is not administering it (migration 0014). The
   * three employee roles hold it and neither portal role does — that division
   * is the capability's entire purpose, since an uncapabilitied people list
   * would show a client every other client's contacts.
   */
  'person.view',
  'user.manage',
  'user.deactivate',
  'org.manage',
  'admin.override',
  'audit.view',
  // Phase 2 §6. `project.manage` authorizes a project *and its paired spaces*
  // on its own, which is what lets staff create projects while `space.manage`
  // stays admin-only.
  'project.manage',
  'project.view',
] as const;

export type Capability = (typeof CAPABILITIES)[number];

const CAPABILITY_SET: ReadonlySet<string> = new Set(CAPABILITIES);

/** Narrows an arbitrary string — a capability column, a route parameter. */
export function isCapability(value: string): value is Capability {
  return CAPABILITY_SET.has(value);
}

/**
 * §4.4. Each role's base capability set; per-person entries are stored
 * separately as explicit deltas in `access_grant`.
 *
 * A baseline says what a person may do, never where — §4.4: "A role baseline
 * grants a capability only where the person also has a grant reaching the
 * node." Holding a capability here is necessary and not sufficient.
 */
export const ROLE_BASELINE: Readonly<Record<UserRole, readonly Capability[]>> = {
  admin: [
    'document.view',
    'document.comment',
    'document.suggest',
    'document.edit',
    'document.publish',
    'document.approve',
    'file.upload',
    'file.delete',
    'node.create',
    'node.delete',
    'space.manage',
    'space.grant',
    'person.view',
    'user.manage',
    'user.deactivate',
    'org.manage',
    'admin.override',
    'audit.view',
    'project.manage',
    'project.view',
  ],
  staff: [
    'document.view',
    'document.comment',
    'document.suggest',
    'document.edit',
    'document.publish',
    'document.approve',
    'file.upload',
    'file.delete',
    'node.create',
    'node.delete',
    'space.grant',
    'person.view',
    // §3.1 — `org.manage` authorizes the paired organization-and-space
    // creation on its own, which is why staff hold it without `space.manage`.
    'org.manage',
    // Phase 2 §4.2, for the same reason and with the same shape.
    'project.manage',
    'project.view',
  ],
  // §10.3 — contractors hold `document.edit`, ratified.
  contractor: [
    'document.view',
    'document.comment',
    'document.suggest',
    'document.edit',
    'file.upload',
    'node.create',
    'person.view',
    'project.view',
  ],
  // §10.6 — `file.upload` from the start, ratified.
  // Phase 2 §6 — neither client role gains anything but `project.view`.
  // `space.grant` and `document.approve` were both proposed for `portal_admin`
  // and both withdrawn (§4.5): clients do not administer projects, and
  // `file.upload` is still the only difference between the two.
  portal_admin: [
    'document.view',
    'document.comment',
    'document.suggest',
    'file.upload',
    'project.view',
  ],
  portal_user: ['document.view', 'document.comment', 'document.suggest', 'project.view'],
} as const;

const BASELINE_SETS: Readonly<Record<UserRole, ReadonlySet<Capability>>> = {
  admin: new Set(ROLE_BASELINE.admin),
  staff: new Set(ROLE_BASELINE.staff),
  contractor: new Set(ROLE_BASELINE.contractor),
  portal_admin: new Set(ROLE_BASELINE.portal_admin),
  portal_user: new Set(ROLE_BASELINE.portal_user),
};

/**
 * Whether a role's baseline carries a capability at all.
 *
 * **Not an access check.** It answers "may this role ever do this", not "may
 * this person do this here" — that is `can` / `canOnSpace`, which read the role
 * from the database themselves. Use this to grey out a button, never to
 * authorize a mutation on a node.
 */
export function roleHasCapability(role: UserRole, capability: Capability): boolean {
  return BASELINE_SETS[role].has(capability);
}
